import React from 'react';

import { Nullable } from '@hrlogics/helper-types';

import {
  GetActivityLogsListDocument,
  GetActivityLogsListQuery,
  GetActivityLogsListQueryVariables,
  useActivityLogDeleteMutation,
} from 'src/api';

import { useNotification } from '../app';

export const useActivityLogDeleteAction = (
  activityLogId: string,
  inboxItemId: Nullable<string>,
) => {
  const { createApolloContext } = useNotification();

  const [deleteActivityLogMutation, { loading: deleting }] = useActivityLogDeleteMutation({
    onCompleted: (_data, clientOptions) => {
      const apolloClient = clientOptions?.client;
      if (!apolloClient) return;

      const queryOptions = {
        query: GetActivityLogsListDocument,
        variables: { inboxItemId: inboxItemId || '' },
      };

      const queryData = apolloClient.readQuery<
        GetActivityLogsListQuery,
        GetActivityLogsListQueryVariables
      >(queryOptions);

      if (queryData) {
        apolloClient.writeQuery<GetActivityLogsListQuery, GetActivityLogsListQueryVariables>({
          ...queryOptions,
          data: {
            getActivityLogsList: queryData.getActivityLogsList.filter(
              activityLogInCache => activityLogInCache.id !== activityLogId,
            ),
          },
        });
      }
    },
    context: createApolloContext({
      successMessage: 'Activity log deleted successfully.',
      errorMessage: 'Failed to delete activity log. Please try again.',
    }),
  });

  const deleteActivityLog = React.useCallback(async () => {
    await deleteActivityLogMutation({
      variables: {
        where: {
          id: activityLogId,
        },
      },
    });
  }, [activityLogId, deleteActivityLogMutation]);

  return {
    deleteActivityLog,
    deleting,
  };
};
